import { orderRowKey } from '../../unifiedOrders';
import { t } from 'i18next';
import { ButtonHTMLAttributes, useEffect, useState } from 'react';
import styled from 'styled-components';
import Modal from '../../../../../components/Modal';
import Tooltip from '../../../../../components/Tooltip';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { FiMapPin } from 'react-icons/fi';
import checkPermission from '../../../../../utils/checkPermission';
import OrderEditorModal from '../OrderEditorModal';
import {
  ApprovalIcon,
  CheckClipboardIcon,
  DownloadIcon,
  EditIcon,
  EmailIcon,
  SplitIcon,
  ViewDetailsIcon,
} from '../../../../../icons';
import { downlaodOrderApi } from '../../../../../services/requests/orders/downloadOrder';
import { EmailLog } from '../../../../../types/apiResponse/email';
import { Order } from '../../../../../types/apiResponse/order';
import BudgeToOrderModal from '../BudgetToOrderModal';
import OrderDetailsModal from '../OrderDetailsModal';
import { OrderDetailsTab } from '../OrderDetailsModal/OrderDetailsContent';
import { orderApprovalColor } from '../../../../../utils/orderApprovalColor';
import OrderEmailLogModal from '../OrderEmailLogModal';
import OrderFractionalItemsModal from '../OrderFractionalItemsModal';
import OrderLocationModal from '../OrderLocationModal';
import { getOrderCoordinates } from '../OrderLocationModal/location';

const ActionTooltip = styled(Tooltip)`
  width: auto;
  display: inline-flex;
  flex-shrink: 0;
`;

function OrderActionButton({ title, children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <ActionTooltip title={title}>
    <button type="button" aria-label={title} {...props}>{children}</button>
  </ActionTooltip>;
}

type Props = {
  orders: Order[];
  className?: string;
  reloadOrders: () => void;
  isEmailSendingUsedWhenMakeOrder: boolean;
  selectedItems?: any[];
  onChangeSelectedItems?: (value: any) => void;
};

const OrdersTableActions = ({
  orders,
  className = '',
  reloadOrders,
  isEmailSendingUsedWhenMakeOrder,
  selectedItems = undefined,
  onChangeSelectedItems = undefined,
}: Props) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [blockedReason, setBlockedReason] = useState('');
  const canView = checkPermission('order-view', { allowTenantAdmin: true });
  const canEdit = checkPermission('order-edit');

  const [initialDetailsTab, setInitialDetailsTab] = useState<OrderDetailsTab>('summary');
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [showOrderEditor, setShowOrderEditor] = useState(false);
  const [showOrderLocationModal, setShowOrderLocationModal] = useState(false);
  const [showOrderFractionalItemsModal, setShowOrderFractionalItemsModal] = useState(false);
  const [showBudgeToOrderModal, setShowBudgeToOrderModal] = useState(false);
  const [isVisibleEmailLogModal, setIsVisibleEmailLogModal] = useState(false);
  const [orderToAction, setOrderToAction] = useState<Order | undefined>();

  const editBlockedReason = (order: Order): string => {
    if (order.history_only) return 'orderEditExportBlocked';
    if (!canEdit) return 'orderEditPermissionRequired';
    if (order.ExportadoErp !== 'n') return 'orderEditExportBlocked';
    if (!order.pendencies?.some(({ status }) => status === 'pending' || status === 'rejected')) {
      return 'orderEditPendencyRequired';
    }
    return '';
  };

  const viewDetailsIconHandleClick = (order: Order, tab: OrderDetailsTab = 'summary') => {
    if (!canView) {
      setBlockedReason(t('orderViewPermissionRequired'));
      return;
    }
    setInitialDetailsTab(tab);
    setShowOrderDetailsModal(true);
    setOrderToAction(order);
  };

  const locationIconHandleClick = (order: Order) => {
    setOrderToAction(order);
    setShowOrderLocationModal(true);
  };

  const fractionationIconHandleClick = (order: Order) => {
    setShowOrderFractionalItemsModal(true);
    setOrderToAction(order);
  };

  const handleClickBudgeToOrder = (order: Order) => {
    setShowBudgeToOrderModal(true);
    setOrderToAction(order);
  };

  const handleClickViewEmailLogIcon = (order: Order) => {
    setIsVisibleEmailLogModal(true);
    setOrderToAction(order);
  };

  const handleActionsCallback = () => {
    reloadOrders();
  };

  if (orders.length === 0) {
    return null;
  }

  const wasTheLastEmailSentSuccessfully = (emailLog: EmailLog[]) => {
    const theLastEmail = emailLog[0];

    if (theLastEmail.motivoErro === '' || theLastEmail.motivoErro === 'Enviado') {
      return true;
    }

    return false;
  };

  const handleEmailIconColor = (order: Order): string => {
    if (!order.email_log || order.email_log.length === 0) {
      return '';
    }

    if (wasTheLastEmailSentSuccessfully(order.email_log)) {
      return 'positive';
    } else {
      return 'negative';
    }
  };

  const handleSelectAll = (checked: boolean) => {
    const updatedSelectedItems = checked ? [...orders] : [];
    if (onChangeSelectedItems) {
      onChangeSelectedItems(updatedSelectedItems);
    }
  };

  const handleCheckboxChange = (checked: boolean, item: Order) => {
    if (selectedItems && onChangeSelectedItems) {
      const updatedSelectedItems = checked
        ? [...selectedItems, item]
        : selectedItems.filter((selectedItem: any) => selectedItem !== item);

      onChangeSelectedItems(updatedSelectedItems);
    }
  };

  const isSelected = (order: Order) => {
    return selectedItems?.some((item) => orderRowKey(item) === orderRowKey(order)) || false;
  };

  const isAllSelected = selectedItems?.length === orders.length && orders.length > 0;

  useEffect(() => {
    if (onChangeSelectedItems) {
      onChangeSelectedItems(selectedItems);
    }

    return () => {
      if (onChangeSelectedItems) {
        onChangeSelectedItems([]);
      }
    };
  }, [selectedItems]);

  const sectionClass = `orders-section actions-section ${isCollapsed ? 'collapsed' : ''}`;

  return (
    <div className={`${sectionClass} ${className}`}>
      {/* Section Header */}
      <div className="section-header">
        <button
          className="section-toggle"
          onClick={() => setIsCollapsed(!isCollapsed)}
          title={isCollapsed ? (t('expand') as string) : (t('collapse') as string)}
        >
          {isCollapsed ? <MdChevronRight /> : <MdChevronLeft />}
        </button>
        <div className="section-title">
          {t('actions') as string}
        </div>
      </div>

      {/* Section Content */}
      <div className="section-content">
        {/* Column Header */}
        <div className="column-header">
          {onChangeSelectedItems && (
            <input
              type="checkbox"
              className="select-all-checkbox checkbox"
              onChange={(e) => handleSelectAll(e.target.checked)}
              checked={isAllSelected}
              title={t('selectAll')}
            />
          )}
        </div>

        {/* Rows */}
        {orders.map((order, index) => (
          <div
            className={`row ${index % 2 === 0 ? 'odd' : ''} ${isSelected(order) ? 'selected' : ''}`}
            key={orderRowKey(order)}
          >
            <div className="action-cell">
              {onChangeSelectedItems && (
                <input
                  type="checkbox"
                  className="checkbox"
                  onChange={(e) => handleCheckboxChange(e.target.checked, order)}
                  checked={isSelected(order)}
                />
              )}

              {!order.history_only && <OrderActionButton
                className={`action-button ${orderApprovalColor(order.pendencies ?? [])}`}
                onClick={() => viewDetailsIconHandleClick(order, 'approval')}
                style={{ opacity: canView ? 1 : 0.5 }}
                title={t('orderApproval')}
                aria-label={t('orderApproval')}
              >
                <ApprovalIcon size="1rem" />
              </OrderActionButton>}

              {!order.history_only && <OrderActionButton
                className="action-button"
                title={t('orderEditAction')}
                style={{ opacity: editBlockedReason(order) ? 0.5 : 1 }}
                onClick={() => {
                  const reason = editBlockedReason(order);
                  if (reason) { setBlockedReason(t(reason)); return; }
                  setOrderToAction(order);
                  setShowOrderEditor(true);
                }}
              >
                <EditIcon size="1rem" />
              </OrderActionButton>}

              <OrderActionButton
                className="action-button"
                onClick={() => viewDetailsIconHandleClick(order)}
                style={{ opacity: canView ? 1 : 0.5 }}
                title={t('viewDetails')}
              >
                <ViewDetailsIcon size="1rem" />
              </OrderActionButton>

              {getOrderCoordinates(order) && (
                <OrderActionButton
                  className="action-button"
                  onClick={() => locationIconHandleClick(order)}
                  title={t('orderLocation')}
                  aria-label={t('orderLocation')}
                >
                  <FiMapPin
                    className="icon"
                    style={{ width: '1rem', height: '1rem', fill: 'none' }}
                    aria-hidden="true"
                  />
                </OrderActionButton>
              )}

              {!order.history_only && <OrderActionButton
                className="action-button"
                onClick={() => fractionationIconHandleClick(order)}
                title={t('fractionation')}
              >
                <SplitIcon size="1rem" />
              </OrderActionButton>}

              {!order.history_only && isEmailSendingUsedWhenMakeOrder && (
                <OrderActionButton
                  className={`action-button ${handleEmailIconColor(order)}`}
                  onClick={() => handleClickViewEmailLogIcon(order)}
                  title={t('emailLog')}
                >
                  <EmailIcon size="1rem" />
                </OrderActionButton>
              )}

              {!order.history_only && order.NumeroPedido && <OrderActionButton
                className="action-button"
                onClick={() => downlaodOrderApi(order.NumeroPedido!)}
                title="PDF Download"
              >
                <DownloadIcon size="1rem" />
              </OrderActionButton>}

              {!order.history_only && order.EOrcamento === 's' && (
                <OrderActionButton
                  className="action-button warning"
                  onClick={() => handleClickBudgeToOrder(order)}
                  title={t('convertBudgetToOrder')}
                >
                  <CheckClipboardIcon size="1rem" style={{ color: 'inherit', fill: 'currentColor' }} />
                </OrderActionButton>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modals */}
      {blockedReason && <Modal title={t('orderActionUnavailable')} showModal onlyCloseButton
        closeModal={() => setBlockedReason('')}>
        <div style={{ padding: '1rem' }}>
          <p role="alert">{blockedReason}</p>
        </div>
      </Modal>}
      {orderToAction && showOrderEditor && <OrderEditorModal order={orderToAction} onSaved={reloadOrders}
        onClose={() => { setShowOrderEditor(false); setOrderToAction(undefined); }} />}
      {orderToAction && showOrderDetailsModal && (
        <OrderDetailsModal
          showModal={showOrderDetailsModal}
          initialTab={initialDetailsTab}
          onPendenciesChanged={reloadOrders}
          closeModal={() => {
            setOrderToAction(undefined);
            setShowOrderDetailsModal(false);
          }}
          order={orderToAction}
        />
      )}

      {orderToAction && showOrderLocationModal && (
        <OrderLocationModal
          showModal={showOrderLocationModal}
          closeModal={() => {
            setOrderToAction(undefined);
            setShowOrderLocationModal(false);
          }}
          order={orderToAction}
        />
      )}

      {orderToAction && showOrderFractionalItemsModal && (
        <OrderFractionalItemsModal
          showModal={showOrderFractionalItemsModal}
          closeModal={() => {
            setOrderToAction(undefined);
            setShowOrderFractionalItemsModal(false);
          }}
          order={orderToAction}
        />
      )}

      {orderToAction && showBudgeToOrderModal && (
        <BudgeToOrderModal
          showModal={showBudgeToOrderModal}
          closeModal={() => {
            setOrderToAction(undefined);
            setShowBudgeToOrderModal(false);
          }}
          callback={handleActionsCallback}
          order={orderToAction}
        />
      )}

      {orderToAction && isVisibleEmailLogModal && (
        <OrderEmailLogModal
          showModal={isVisibleEmailLogModal}
          closeModal={() => {
            setOrderToAction(undefined);
            setIsVisibleEmailLogModal(false);
          }}
          order={orderToAction}
        />
      )}
    </div>
  );
};

export default OrdersTableActions;
